export const MARKDOWN_EXTENSIONS: readonly string[] = Object.freeze([
    '.md',
    '.markdown',
    '.mdown',
    '.mkdn',
    '.mkd',
    '.mdwn',
    '.mdtxt',
    '.mdtext',
    '.text',
]);

export const MDX_EXTENSIONS: readonly string[] = Object.freeze(['.mdx']);

export const MARKDOWN_MIME_TYPES: readonly string[] = Object.freeze([
    'text/markdown',
    'text/x-markdown',
    'text/md',
    'text/plain',
    'application/octet-stream',
    '',
]);

export const MARKDOWN_ACCEPT: string = [...MARKDOWN_EXTENSIONS, 'text/markdown', 'text/x-markdown'].join(',');

const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;
const DEFAULT_SAMPLE_CHARS = 512_000;
const DEFAULT_MIN_CONFIDENCE = 0.5;

export type ValidationMode = 'lenient' | 'strict';
export type ExtensionPolicy = 'require' | 'warn' | 'ignore';
export type LineEndings = 'lf' | 'crlf' | 'cr' | 'mixed' | 'none';
export type TextEncoding = 'utf-8' | 'utf-16le' | 'utf-16be' | 'windows-1252' | 'string';

export type DetectedFormat =
    | 'pdf'
    | 'zip'
    | 'ole'
    | 'rtf'
    | 'image'
    | 'archive'
    | 'executable'
    | 'database'
    | 'binary'
    | 'html'
    | 'xml'
    | 'json'
    | 'csv'
    | 'source-code';

export type IssueCode =
    | 'INVALID_INPUT'
    | 'READ_FAILED'
    | 'ABORTED'
    | 'EMPTY'
    | 'TOO_LARGE'
    | 'BAD_EXTENSION'
    | 'NO_FILENAME'
    | 'UNEXPECTED_MIME'
    | 'BINARY'
    | 'ENCODING'
    | 'LEGACY_ENCODING'
    | 'FOREIGN_FORMAT'
    | 'LOOKS_LIKE_OTHER_FORMAT'
    | 'NOT_MARKDOWN'
    | 'PLAIN_TEXT'
    | 'MDX_SYNTAX'
    | 'UNCLOSED_FENCE'
    | 'MIXED_LINE_ENDINGS'
    | 'EMPTY_BODY';

export interface ValidationIssue {
    code: IssueCode;
    message: string;
    detail?: Record<string, unknown>;
}

export interface FileMeta {
    name: string;
    extension: string;
    size: number;
    type: string;
}

export interface MarkdownSignals {
    frontMatter: boolean;
    atxHeadings: number;
    setextHeadings: number;
    listItems: number;
    taskItems: number;
    blockquotes: number;
    fencedCodeBlocks: number;
    inlineCode: number;
    emphasis: number;
    strikethrough: number;
    links: number;
    wikiLinks: number;
    images: number;
    autolinks: number;
    referenceDefinitions: number;
    tables: number;
    horizontalRules: number;
    footnotes: number;
    htmlTagLines: number;
    mdxSyntax: number;
    signalLines: number;
    nonEmptyLines: number;
}

export interface MarkdownAnalysis {
    signals: MarkdownSignals;
    confidence: number;
    foreignFormat: 'html' | 'json' | 'xml' | 'rtf' | null;
    lookAlike: 'html' | 'csv' | 'source-code' | null;
    unclosedFence: boolean;
    emptyBody: boolean;
    lineEndings: LineEndings;
    lineCount: number;
}

export interface MarkdownValidationOptions {
    mode?: ValidationMode;
    minConfidence?: number;
    maxBytes?: number;
    allowEmpty?: boolean;
    extensionPolicy?: ExtensionPolicy;
    allowedExtensions?: readonly string[];
    allowMdx?: boolean;
    allowLegacyEncoding?: boolean;
    sampleChars?: number;
    includeContent?: boolean;
    signal?: AbortSignal;
}

export interface MarkdownValidationResult {
    valid: boolean;
    summary: string;
    errors: ValidationIssue[];
    warnings: ValidationIssue[];
    confidence: number;
    signals: MarkdownSignals;
    detectedFormat: DetectedFormat | null;
    encoding: TextEncoding;
    hadBom: boolean;
    lineEndings: LineEndings;
    lineCount: number;
    file: FileMeta;
    content?: string;
}

interface ResolvedOptions {
    mode: ValidationMode;
    minConfidence: number;
    maxBytes: number;
    allowEmpty: boolean;
    extensionPolicy: ExtensionPolicy;
    allowedExtensions: Set<string>;
    allowMdx: boolean;
    allowLegacyEncoding: boolean;
    sampleChars: number;
    includeContent: boolean;
    signal?: AbortSignal;
}

class IssueList {
    readonly errors: ValidationIssue[] = [];
    readonly warnings: ValidationIssue[] = [];

    error(code: IssueCode, message: string, detail?: Record<string, unknown>): void {
        this.errors.push(detail ? { code, message, detail } : { code, message });
    }

    warn(code: IssueCode, message: string, detail?: Record<string, unknown>): void {
        this.warnings.push(detail ? { code, message, detail } : { code, message });
    }
}

interface Draft {
    file: FileMeta;
    issues: IssueList;
    encoding: TextEncoding;
    hadBom: boolean;
    detectedFormat: DetectedFormat | null;
    analysis: MarkdownAnalysis | null;
    content?: string;
}

interface MagicSignature {
    bytes: number[];
    format: DetectedFormat;
    label: string;
}

interface BomInfo {
    encoding: 'utf-8' | 'utf-16le' | 'utf-16be' | null;
    length: number;
}

const MAGIC_SIGNATURES: readonly MagicSignature[] = [
    { bytes: [0x25, 0x50, 0x44, 0x46, 0x2d], format: 'pdf', label: 'PDF document' },
    { bytes: [0x50, 0x4b, 0x03, 0x04], format: 'zip', label: 'ZIP-based file' },
    { bytes: [0x50, 0x4b, 0x05, 0x06], format: 'zip', label: 'ZIP archive' },
    { bytes: [0x50, 0x4b, 0x07, 0x08], format: 'zip', label: 'ZIP archive' },
    { bytes: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1], format: 'ole', label: 'legacy Office document' },
    { bytes: [0x7b, 0x5c, 0x72, 0x74, 0x66], format: 'rtf', label: 'RTF document' },
    { bytes: [0x89, 0x50, 0x4e, 0x47], format: 'image', label: 'PNG image' },
    { bytes: [0xff, 0xd8, 0xff], format: 'image', label: 'JPEG image' },
    { bytes: [0x47, 0x49, 0x46, 0x38], format: 'image', label: 'GIF image' },
    { bytes: [0x1f, 0x8b], format: 'archive', label: 'gzip archive' },
    { bytes: [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c], format: 'archive', label: '7-Zip archive' },
    { bytes: [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07], format: 'archive', label: 'RAR archive' },
    { bytes: [0x7f, 0x45, 0x4c, 0x46], format: 'executable', label: 'ELF executable' },
    {
        bytes: [0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74],
        format: 'database',
        label: 'SQLite database',
    },
];

const RE = {
    fenceOpen: /^ {0,3}(`{3,}|~{3,})(.*)$/,
    atx: /^ {0,3}#{1,6}(?:[ \t]|$)/,
    hr: /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/,
    setext: /^ {0,3}(?:=+|-+)[ \t]*$/,
    blockquote: /^ {0,3}>/,
    task: /^[ \t]*(?:[-*+]|\d{1,9}[.)])[ \t]+\[[ xX]\](?:[ \t]|$)/,
    list: /^[ \t]*(?:[-*+]|\d{1,9}[.)])[ \t]+\S/,
    tableDelimiter: /^[ \t]*\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/,
    refDef: /^ {0,3}\[[^\]]+\]:[ \t]*\S/,
    htmlLine: /^[ \t]*(?:<\/?[a-zA-Z][a-zA-Z0-9-]*(?:[ \t][^>]*)?\/?>|<!--)/,
    mdxImportExport: /^(?:import[ \t]+(?:[\w*{}\s,]+[ \t]+from[ \t]+)?['"]|export[ \t]+(?:const|let|var|default|function|class|async)[ \t])/,
    jsxComponent: /<[A-Z][A-Za-z0-9.]+(?:\s[^>]*)?\/?>/,
    codeLike: /(?:[;{}]|\)\s*\{)\s*$|^\s*(?:import\s|#include\b|package\s|using\s|namespace\s|def\s|class\s|function\s|public\s|private\s|protected\s|const\s|let\s|var\s|return\b|if\s*\(|for\s*\(|while\s*\(|#!\/)/,
    inlineCode: /`[^`\n]+`/g,
    image: /!\[[^\]\n]*\]\([^\s)]+(?:[ \t]+"[^"\n]*")?\)/g,
    link: /\[[^\]\n]*\]\([^\s)]+(?:[ \t]+"[^"\n]*")?\)/g,
    refLink: /\[[^\]\n]+\]\[[^\]\n]*\]/g,
    wikiLink: /\[\[[^\]\n]+\]\]/g,
    autolink: /<https?:\/\/[^\s>]+>/g,
    strongStar: /\*\*[^\s*](?:[^\n*]*?[^\s*])?\*\*/g,
    strongUnder: /(?:^|[^A-Za-z0-9_])__[^\s_](?:[^\n_]*?[^\s_])?__(?![A-Za-z0-9_])/g,
    emStar: /(?:^|[^*])\*[^\s*](?:[^\n*]*?[^\s*])?\*(?!\*)/g,
    emUnder: /(?:^|[^A-Za-z0-9_])_[^\s_](?:[^\n_]*?[^\s_])?_(?![A-Za-z0-9_])/g,
    strike: /~~[^\s~](?:[^\n~]*?[^\s~])?~~/g,
    footnote: /\[\^[^\]\s]+\]/g,
};

const FOREIGN_MESSAGES: Record<NonNullable<MarkdownAnalysis['foreignFormat']>, string> = {
    html: 'This is an HTML page, not a Markdown document.',
    xml: 'This is an XML document, not a Markdown document.',
    json: 'This is a JSON document, not a Markdown document.',
    rtf: 'This is an RTF document, not a Markdown document.',
};

const LOOKALIKE_MESSAGES: Record<NonNullable<MarkdownAnalysis['lookAlike']>, string> = {
    html: 'The content looks like an HTML fragment rather than Markdown.',
    csv: 'The content looks like delimited data rather than Markdown.',
    'source-code': 'The content looks like source code rather than Markdown.',
};

export async function validateMarkdownFile(
    input: File | Blob,
    options: MarkdownValidationOptions = {},
): Promise<MarkdownValidationResult> {
    const opts = resolveOptions(options);
    const draft = createDraft(describeBlob(input));
    const { issues, file } = draft;

    if (!(typeof Blob !== 'undefined' && input instanceof Blob)) {
        issues.error('INVALID_INPUT', 'Expected a File or Blob.');
        return build(draft);
    }

    if (file.size > opts.maxBytes) {
        issues.error('TOO_LARGE', `The file is ${formatBytes(file.size)}; the limit is ${formatBytes(opts.maxBytes)}.`, {
            size: file.size,
            maxBytes: opts.maxBytes,
        });
        return build(draft);
    }
    checkExtension(file, opts, issues, typeof (input as File).name === 'string');
    checkMime(file, issues);

    if (file.size === 0) {
        if (!opts.allowEmpty) issues.error('EMPTY', 'The file is empty.');
        if (opts.includeContent) draft.content = '';
        return build(draft);
    }

    let bytes: Uint8Array;
    try {
        bytes = new Uint8Array(await readBytes(input, opts.signal));
    } catch (error) {
        issues.error(isAbortError(error) || opts.signal?.aborted ? 'ABORTED' : 'READ_FAILED', errorMessage(error));
        return build(draft);
    }
    if (opts.signal?.aborted) {
        issues.error('ABORTED', 'Validation was cancelled.');
        return build(draft);
    }

    const magic = sniffMagicBytes(bytes);
    if (magic) {
        draft.detectedFormat = magic.format;
        issues.error('FOREIGN_FORMAT', `This is a ${magic.label}, not a Markdown document.`, { format: magic.format });
        return build(draft);
    }

    const bom = detectBom(bytes);
    draft.hadBom = bom.encoding !== null;
    if (!draft.hadBom && looksBinary(bytes)) {
        draft.detectedFormat = 'binary';
        issues.error('BINARY', 'The file contains binary data, so it cannot be a Markdown document.');
        return build(draft);
    }

    const decoded = decodeBytes(bytes, bom, opts, issues);
    if (!decoded) return build(draft);
    draft.encoding = decoded.encoding;

    return validateText(decoded.text, opts, draft);
}

export function validateMarkdownFiles(
    files: Iterable<File | Blob> | ArrayLike<File | Blob>,
    options: MarkdownValidationOptions = {},
): Promise<MarkdownValidationResult[]> {
    return Promise.all(Array.from(files as Iterable<File | Blob>, (file) => validateMarkdownFile(file, options)));
}

export function validateMarkdownText(
    text: string,
    options: MarkdownValidationOptions = {},
    meta: { name?: string; type?: string } = {},
): MarkdownValidationResult {
    const opts = resolveOptions(options);
    const name = meta.name ?? '';
    const draft = createDraft({
        name,
        extension: getExtension(name),
        size: typeof text === 'string' ? utf8Length(text) : 0,
        type: normalizeMime(meta.type ?? ''),
    });
    draft.encoding = 'string';

    if (typeof text !== 'string') {
        draft.issues.error('INVALID_INPUT', 'Expected a string.');
        return build(draft);
    }
    if (draft.file.size > opts.maxBytes) {
        draft.issues.error('TOO_LARGE', `The text is ${formatBytes(draft.file.size)}; the limit is ${formatBytes(opts.maxBytes)}.`, {
            size: draft.file.size,
            maxBytes: opts.maxBytes,
        });
        return build(draft);
    }
    if (name) checkExtension(draft.file, opts, draft.issues, true);
    if (meta.type) checkMime(draft.file, draft.issues);
    return validateText(text, opts, draft);
}

export function analyzeMarkdown(
    text: string,
    options: Pick<MarkdownValidationOptions, 'sampleChars' | 'allowMdx'> = {},
): MarkdownAnalysis {
    const lineEndings = detectLineEndings(text);
    const normalized = normalizeLineEndings(text, lineEndings);
    return analyzeNormalized(normalized, options.sampleChars ?? DEFAULT_SAMPLE_CHARS, options.allowMdx ?? false, lineEndings);
}

export function isMarkdownFileName(name: string, allowedExtensions: readonly string[] = MARKDOWN_EXTENSIONS): boolean {
    const ext = getExtension(name);
    return ext !== '' && allowedExtensions.some((allowed) => normalizeExtension(allowed) === ext);
}

function resolveOptions(options: MarkdownValidationOptions): ResolvedOptions {
    const allowMdx = options.allowMdx ?? false;
    const allowedExtensions = new Set((options.allowedExtensions ?? MARKDOWN_EXTENSIONS).map(normalizeExtension));
    if (allowMdx) MDX_EXTENSIONS.forEach((extension) => allowedExtensions.add(extension));

    return {
        mode: options.mode ?? 'lenient',
        minConfidence: clamp01(options.minConfidence ?? DEFAULT_MIN_CONFIDENCE),
        maxBytes: options.maxBytes ?? DEFAULT_MAX_BYTES,
        allowEmpty: options.allowEmpty ?? false,
        extensionPolicy: options.extensionPolicy ?? 'require',
        allowedExtensions,
        allowMdx,
        allowLegacyEncoding: options.allowLegacyEncoding ?? false,
        sampleChars: Math.max(1000, options.sampleChars ?? DEFAULT_SAMPLE_CHARS),
        includeContent: options.includeContent ?? true,
        signal: options.signal,
    };
}

function createDraft(file: FileMeta): Draft {
    return { file, issues: new IssueList(), encoding: 'utf-8', hadBom: false, detectedFormat: null, analysis: null };
}

function build(draft: Draft): MarkdownValidationResult {
    const analysis = draft.analysis ?? emptyAnalysis();
    const valid = draft.issues.errors.length === 0;
    const result: MarkdownValidationResult = {
        valid,
        summary: valid ? successSummary(analysis.confidence, draft.issues.warnings.length) : draft.issues.errors[0].message,
        errors: draft.issues.errors,
        warnings: draft.issues.warnings,
        confidence: analysis.confidence,
        signals: analysis.signals,
        detectedFormat: draft.detectedFormat,
        encoding: draft.encoding,
        hadBom: draft.hadBom,
        lineEndings: analysis.lineEndings,
        lineCount: analysis.lineCount,
        file: draft.file,
    };
    if (draft.content !== undefined) result.content = draft.content;
    return result;
}

function successSummary(confidence: number, warningCount: number): string {
    const base =
        confidence >= 0.8
            ? 'Valid Markdown (high confidence)'
            : confidence >= 0.5
                ? 'Valid Markdown (medium confidence)'
                : confidence > 0
                    ? 'Valid Markdown (low confidence; little Markdown syntax found)'
                    : 'Valid text document (no Markdown syntax found)';
    return warningCount ? `${base}, ${warningCount} warning${warningCount === 1 ? '' : 's'}.` : `${base}.`;
}

function describeBlob(input: unknown): FileMeta {
    const blob = typeof Blob !== 'undefined' && input instanceof Blob ? input : null;
    const name = blob && typeof (blob as File).name === 'string' ? (blob as File).name : '';
    return { name, extension: getExtension(name), size: blob?.size ?? 0, type: normalizeMime(blob?.type ?? '') };
}

function checkExtension(file: FileMeta, opts: ResolvedOptions, issues: IssueList, hasName: boolean): void {
    if (opts.extensionPolicy === 'ignore') return;
    if (!hasName) {
        if (opts.extensionPolicy === 'require') {
            issues.warn('NO_FILENAME', 'The input has no file name, so its extension could not be checked.');
        }
        return;
    }
    if (opts.allowedExtensions.has(file.extension)) return;

    const allowed = [...opts.allowedExtensions].join(', ');
    const message = file.extension
        ? `"${file.extension}" is not a Markdown file extension (expected one of: ${allowed}).`
        : `The file name has no extension (expected one of: ${allowed}).`;
    const detail = { extension: file.extension, allowed: [...opts.allowedExtensions] };
    if (opts.extensionPolicy === 'require') issues.error('BAD_EXTENSION', message, detail);
    else issues.warn('BAD_EXTENSION', message, detail);
}

function checkMime(file: FileMeta, issues: IssueList): void {
    if (MARKDOWN_MIME_TYPES.includes(file.type)) return;
    issues.warn('UNEXPECTED_MIME', `The browser reported the type "${file.type}", which is unusual for Markdown.`, {
        type: file.type,
    });
}

function readBytes(blob: Blob, signal?: AbortSignal): Promise<ArrayBuffer> {
    if (signal?.aborted) return Promise.reject(abortError());
    if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();

    return new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        const onAbort = (): void => {
            reader.abort();
            reject(abortError());
        };
        const cleanup = (): void => signal?.removeEventListener('abort', onAbort);
        signal?.addEventListener('abort', onAbort, { once: true });
        reader.onload = () => {
            cleanup();
            resolve(reader.result as ArrayBuffer);
        };
        reader.onerror = () => {
            cleanup();
            reject(reader.error ?? new Error('unknown FileReader error'));
        };
        reader.readAsArrayBuffer(blob);
    });
}

function sniffMagicBytes(bytes: Uint8Array): MagicSignature | null {
    for (const signature of MAGIC_SIGNATURES) {
        if (bytes.length >= signature.bytes.length && signature.bytes.every((byte, index) => bytes[index] === byte)) {
            return signature;
        }
    }

    if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
        const subtype = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
        if (subtype === 'WEBP') return { bytes: [], format: 'image', label: 'WebP image' };
        if (subtype === 'WAVE' || subtype === 'AVI ') return { bytes: [], format: 'binary', label: 'RIFF media file' };
    }
    return null;
}

function detectBom(bytes: Uint8Array): BomInfo {
    if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
        return { encoding: 'utf-8', length: 3 };
    }
    if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) return { encoding: 'utf-16le', length: 2 };
    if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) return { encoding: 'utf-16be', length: 2 };
    return { encoding: null, length: 0 };
}

function looksBinary(bytes: Uint8Array): boolean {
    const end = Math.min(bytes.length, 8192);
    let suspicious = 0;
    for (let index = 0; index < end; index++) {
        const byte = bytes[index];
        if (byte === 0) return true;
        if ((byte < 0x20 && byte !== 0x09 && byte !== 0x0a && byte !== 0x0d && byte !== 0x0c) || byte === 0x7f) {
            suspicious++;
        }
    }
    return end > 0 && suspicious / end > 0.02;
}

function decodeBytes(
    bytes: Uint8Array,
    bom: BomInfo,
    opts: ResolvedOptions,
    issues: IssueList,
): { text: string; encoding: TextEncoding } | null {
    if (typeof TextDecoder === 'undefined') {
        issues.error('READ_FAILED', 'TextDecoder is not available in this environment.');
        return null;
    }

    const payload = bom.length ? bytes.subarray(bom.length) : bytes;
    const primary = bom.encoding ?? 'utf-8';
    try {
        return { text: new TextDecoder(primary, { fatal: true }).decode(payload), encoding: primary };
    } catch {
        if (primary === 'utf-8' && opts.allowLegacyEncoding) {
            const text = new TextDecoder('windows-1252').decode(payload);
            issues.warn('LEGACY_ENCODING', 'The file is not valid UTF-8 and was decoded as Windows-1252 instead.');
            return { text, encoding: 'windows-1252' };
        }

        issues.error(
            'ENCODING',
            primary === 'utf-8'
                ? 'The file is not valid UTF-8 text. Re-save it with UTF-8 encoding.'
                : `The file has a ${primary.toUpperCase()} byte-order mark but its contents are not valid ${primary.toUpperCase()}.`,
            { encoding: primary },
        );
        return null;
    }
}

function validateText(text: string, opts: ResolvedOptions, draft: Draft): MarkdownValidationResult {
    const lineEndings = detectLineEndings(text);
    const normalized = normalizeLineEndings(text, lineEndings);
    if (opts.includeContent) draft.content = normalized;

    if (normalized.trim().length === 0) {
        draft.analysis = emptyAnalysis(lineEndings);
        if (!opts.allowEmpty) draft.issues.error('EMPTY', 'The file contains only whitespace.');
        return build(draft);
    }
    if (lineEndings === 'mixed') {
        draft.issues.warn('MIXED_LINE_ENDINGS', 'Mixed line endings were found; the returned content is normalised to LF.');
    }

    const analysis = analyzeNormalized(normalized, opts.sampleChars, opts.allowMdx, lineEndings);
    draft.analysis = analysis;

    if (analysis.foreignFormat) {
        draft.detectedFormat = analysis.foreignFormat;
        draft.issues.error('FOREIGN_FORMAT', FOREIGN_MESSAGES[analysis.foreignFormat], { format: analysis.foreignFormat });
        return build(draft);
    }

    if (analysis.lookAlike) {
        draft.detectedFormat = analysis.lookAlike;
        const detail = { format: analysis.lookAlike };
        if (opts.mode === 'strict') draft.issues.error('LOOKS_LIKE_OTHER_FORMAT', LOOKALIKE_MESSAGES[analysis.lookAlike], detail);
        else draft.issues.warn('LOOKS_LIKE_OTHER_FORMAT', LOOKALIKE_MESSAGES[analysis.lookAlike], detail);
    }

    if (analysis.signals.mdxSyntax > 0 && !opts.allowMdx && analysis.lookAlike !== 'source-code') {
        draft.issues.warn('MDX_SYNTAX', 'The content contains MDX syntax that plain Markdown renderers will not understand.', {
            count: analysis.signals.mdxSyntax,
        });
    }
    if (analysis.unclosedFence) draft.issues.warn('UNCLOSED_FENCE', 'A code fence is never closed.');
    if (analysis.emptyBody) draft.issues.warn('EMPTY_BODY', 'The document contains front matter but no content after it.');

    if (analysis.confidence < opts.minConfidence) {
        const detail = { confidence: analysis.confidence, minConfidence: opts.minConfidence };
        if (opts.mode === 'strict') {
            draft.issues.error(
                'NOT_MARKDOWN',
                `No recognisable Markdown syntax was found (confidence ${analysis.confidence.toFixed(2)}, required >= ${opts.minConfidence.toFixed(2)}).`,
                detail,
            );
        } else if (!analysis.lookAlike) {
            draft.issues.warn('PLAIN_TEXT', 'Little or no Markdown syntax was found; the file will behave like plain text.', detail);
        }
    }

    return build(draft);
}

function detectForeignTextFormat(normalized: string): MarkdownAnalysis['foreignFormat'] {
    const head = normalized.slice(0, 1024).replace(/^\uFEFF/, '').trimStart();
    const lower = head.slice(0, 64).toLowerCase();
    if (lower.startsWith('<!doctype html') || /^<html[\s>]/.test(lower)) return 'html';
    if (lower.startsWith('<?xml')) return 'xml';
    if (lower.startsWith('{\\rtf')) return 'rtf';
    if (head[0] === '{' || head[0] === '[') {
        try {
            JSON.parse(normalized);
            return 'json';
        } catch {
            return null;
        }
    }
    return null;
}

function analyzeNormalized(
    normalized: string,
    sampleChars: number,
    allowMdx: boolean,
    lineEndings: LineEndings,
): MarkdownAnalysis {
    const signals = newSignals();
    const sample = normalized.length > sampleChars ? normalized.slice(0, sampleChars) : normalized;
    const lines = sample.split('\n');
    const lineCount = countChar(normalized, '\n') + (normalized.endsWith('\n') ? 0 : 1);
    const foreignFormat = detectForeignTextFormat(normalized);
    const frontMatter = detectFrontMatter(lines);
    signals.frontMatter = frontMatter.present;
    if (frontMatter.present) {
        signals.nonEmptyLines++;
        signals.signalLines++;
    }

    let inFence: string | null = null;
    let prevIsParagraph = false;
    let prevLine = '';
    let codeLikeLines = 0;
    let importExportLines = 0;
    let jsxLines = 0;
    const csvSample: string[] = [];

    for (let index = frontMatter.bodyStart; index < lines.length; index++) {
        const line = lines[index];

        if (inFence !== null) {
            if (isFenceClose(line, inFence)) {
                inFence = null;
                signals.fencedCodeBlocks++;
            }
            continue;
        }

        const fence = matchFenceOpen(line);
        if (fence) {
            inFence = fence;
            signals.nonEmptyLines++;
            signals.signalLines++;
            prevIsParagraph = false;
            prevLine = line;
            continue;
        }

        if (line.trim() === '') {
            prevIsParagraph = false;
            prevLine = line;
            continue;
        }

        signals.nonEmptyLines++;
        if (csvSample.length < 50) csvSample.push(line);

        let kind: 'block' | 'html' | 'text' = 'block';
        if (RE.atx.test(line)) signals.atxHeadings++;
        else if (RE.hr.test(line)) {
            if (prevIsParagraph && /^ {0,3}-/.test(line)) signals.setextHeadings++;
            else signals.horizontalRules++;
        } else if (prevIsParagraph && RE.setext.test(line)) signals.setextHeadings++;
        else if (RE.blockquote.test(line)) signals.blockquotes++;
        else if (RE.task.test(line)) signals.taskItems++;
        else if (RE.list.test(line)) signals.listItems++;
        else if (line.includes('|') && prevLine.includes('|') && RE.tableDelimiter.test(line)) signals.tables++;
        else if (RE.refDef.test(line)) signals.referenceDefinitions++;
        else if (RE.htmlLine.test(line)) {
            signals.htmlTagLines++;
            kind = 'html';
        } else kind = 'text';

        const images = countMatches(RE.image, line);
        const links = countMatches(RE.link, line) - images + countMatches(RE.refLink, line);
        const wikiLinks = countMatches(RE.wikiLink, line);
        const inlineCode = countMatches(RE.inlineCode, line);
        const autolinks = countMatches(RE.autolink, line);
        const emphasis =
            countMatches(RE.strongStar, line) +
            countMatches(RE.strongUnder, line) +
            countMatches(RE.emStar, line) +
            countMatches(RE.emUnder, line);
        const strikethrough = countMatches(RE.strike, line);
        const footnotes = countMatches(RE.footnote, line);

        signals.images += images;
        signals.links += links;
        signals.wikiLinks += wikiLinks;
        signals.inlineCode += inlineCode;
        signals.autolinks += autolinks;
        signals.emphasis += emphasis;
        signals.strikethrough += strikethrough;
        signals.footnotes += footnotes;

        if (RE.mdxImportExport.test(line)) importExportLines++;
        else if (RE.jsxComponent.test(line)) jsxLines++;
        if (RE.codeLike.test(line)) codeLikeLines++;

        const inlineTotal = images + links + wikiLinks + inlineCode + autolinks + emphasis + strikethrough + footnotes;
        if (kind === 'block' || inlineTotal > 0) signals.signalLines++;
        prevIsParagraph = kind === 'text';
        prevLine = line;
    }

    const unclosedFence = inFence !== null;
    if (unclosedFence) signals.fencedCodeBlocks++;
    signals.mdxSyntax = importExportLines + (importExportLines > 0 || jsxLines >= 2 ? jsxLines : 0);

    const blockSignals =
        signals.atxHeadings +
        signals.setextHeadings +
        signals.listItems +
        signals.taskItems +
        signals.blockquotes +
        signals.fencedCodeBlocks +
        signals.tables +
        signals.horizontalRules +
        signals.referenceDefinitions;
    const richSignals =
        signals.listItems + signals.taskItems + signals.tables + signals.links + signals.images + signals.fencedCodeBlocks + signals.blockquotes;

    let lookAlike: MarkdownAnalysis['lookAlike'] = null;
    if (signals.htmlTagLines >= 3 && signals.htmlTagLines / signals.nonEmptyLines >= 0.5 && blockSignals === 0) {
        lookAlike = 'html';
    } else if (codeLikeLines >= 3 && codeLikeLines / signals.nonEmptyLines >= 0.35 && richSignals <= 1) {
        lookAlike = 'source-code';
    } else if (blockSignals === 0 && signals.signalLines === 0 && looksLikeCsv(csvSample)) {
        lookAlike = 'csv';
    }

    return {
        signals,
        confidence: computeConfidence(signals, allowMdx, lookAlike),
        foreignFormat,
        lookAlike,
        unclosedFence,
        emptyBody: frontMatter.present && signals.nonEmptyLines === 0,
        lineEndings,
        lineCount,
    };
}

const SIGNAL_WEIGHTS: ReadonlyArray<readonly [keyof MarkdownSignals, number, number]> = [
    ['atxHeadings', 3, 5],
    ['fencedCodeBlocks', 3, 4],
    ['tables', 3, 3],
    ['taskItems', 3, 3],
    ['images', 2.5, 3],
    ['referenceDefinitions', 2.5, 3],
    ['footnotes', 2.5, 3],
    ['setextHeadings', 2, 3],
    ['links', 2, 5],
    ['wikiLinks', 2, 4],
    ['autolinks', 1.5, 3],
    ['blockquotes', 1.5, 4],
    ['horizontalRules', 1.5, 3],
    ['strikethrough', 1.5, 2],
    ['listItems', 1.2, 6],
    ['inlineCode', 1, 6],
    ['emphasis', 0.8, 6],
];

function computeConfidence(signals: MarkdownSignals, allowMdx: boolean, lookAlike: MarkdownAnalysis['lookAlike']): number {
    let score = 0;
    for (const [key, weight, cap] of SIGNAL_WEIGHTS) score += weight * Math.min(signals[key] as number, cap);
    if (signals.frontMatter) score += 5;
    if (allowMdx) score += 2 * Math.min(signals.mdxSyntax, 2);

    let confidence = 1 - Math.exp(-score / 4);
    const density = signals.signalLines / Math.max(1, signals.nonEmptyLines);
    confidence *= 0.7 + 0.3 * Math.min(1, density * 50);
    if (lookAlike) confidence *= 0.5;
    return Math.round(confidence * 1000) / 1000;
}

function detectFrontMatter(lines: string[]): { present: boolean; bodyStart: number } {
    const opener = lines[0]?.trimEnd();
    if (opener !== '---' && opener !== '+++') return { present: false, bodyStart: 0 };
    const closers = opener === '---' ? ['---', '...'] : ['+++'];
    const limit = Math.min(lines.length, 200);
    let sawKey = false;
    for (let index = 1; index < limit; index++) {
        const line = lines[index].trimEnd();
        if (closers.includes(line)) return sawKey ? { present: true, bodyStart: index + 1 } : { present: false, bodyStart: 0 };
        if (/^[A-Za-z_][\w.-]*\s*[:=]/.test(line)) sawKey = true;
    }
    return { present: false, bodyStart: 0 };
}

function matchFenceOpen(line: string): string | null {
    const match = RE.fenceOpen.exec(line);
    if (!match) return null;
    if (match[1][0] === '`' && match[2].includes('`')) return null;
    return match[1];
}

function isFenceClose(line: string, opener: string): boolean {
    const match = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(line);
    return match !== null && match[1][0] === opener[0] && match[1].length >= opener.length;
}

function looksLikeCsv(samples: string[]): boolean {
    if (samples.length < 5) return false;
    for (const delimiter of [',', '\t', ';']) {
        const counts = samples.map((line) => countChar(line, delimiter));
        if (counts[0] < 1) continue;
        if (counts.filter((count) => count === counts[0]).length / counts.length >= 0.9) return true;
    }
    return false;
}

function newSignals(): MarkdownSignals {
    return {
        frontMatter: false,
        atxHeadings: 0,
        setextHeadings: 0,
        listItems: 0,
        taskItems: 0,
        blockquotes: 0,
        fencedCodeBlocks: 0,
        inlineCode: 0,
        emphasis: 0,
        strikethrough: 0,
        links: 0,
        wikiLinks: 0,
        images: 0,
        autolinks: 0,
        referenceDefinitions: 0,
        tables: 0,
        horizontalRules: 0,
        footnotes: 0,
        htmlTagLines: 0,
        mdxSyntax: 0,
        signalLines: 0,
        nonEmptyLines: 0,
    };
}

function emptyAnalysis(lineEndings: LineEndings = 'none'): MarkdownAnalysis {
    return {
        signals: newSignals(),
        confidence: 0,
        foreignFormat: null,
        lookAlike: null,
        unclosedFence: false,
        emptyBody: false,
        lineEndings,
        lineCount: 0,
    };
}

function detectLineEndings(text: string): LineEndings {
    let crlf = 0;
    let lf = 0;
    let cr = 0;
    for (let index = 0; index < text.length; index++) {
        const char = text.charCodeAt(index);
        if (char === 13) {
            if (text.charCodeAt(index + 1) === 10) {
                crlf++;
                index++;
            } else cr++;
        } else if (char === 10) lf++;
    }
    const kinds = (crlf ? 1 : 0) + (lf ? 1 : 0) + (cr ? 1 : 0);
    if (kinds === 0) return 'none';
    if (kinds > 1) return 'mixed';
    return crlf ? 'crlf' : lf ? 'lf' : 'cr';
}

function normalizeLineEndings(text: string, lineEndings: LineEndings): string {
    return lineEndings === 'lf' || lineEndings === 'none' ? text : text.replace(/\r\n?/g, '\n');
}

function countMatches(regex: RegExp, line: string): number {
    return line.match(regex)?.length ?? 0;
}

function countChar(text: string, char: string): number {
    let count = 0;
    for (let index = text.indexOf(char); index !== -1; index = text.indexOf(char, index + 1)) count++;
    return count;
}

function getExtension(name: string): string {
    const base = name.split(/[\\/]/).pop() ?? '';
    const dot = base.lastIndexOf('.');
    return dot > 0 ? base.slice(dot).toLowerCase() : '';
}

function normalizeExtension(extension: string): string {
    const normalized = extension.trim().toLowerCase();
    return normalized.startsWith('.') ? normalized : `.${normalized}`;
}

function normalizeMime(type: string): string {
    return type.split(';')[0].trim().toLowerCase();
}

function utf8Length(text: string): number {
    return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(text).length : text.length;
}

function clamp01(value: number): number {
    return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : DEFAULT_MIN_CONFIDENCE;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function abortError(): Error {
    const error = new Error('Validation was aborted.');
    error.name = 'AbortError';
    return error;
}

function isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}